import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Checkbox } from '@monksflow/monks-ui';
import { Check } from 'lucide-react';
import { taxonomyLevelLabel } from '@/entities/taxonomy';
import type { TaxonomyListSummary } from '@/entities/taxonomy-list';
import {
  findStandardList,
  standardListKeysFor,
} from '../../../TaxonomyDetail/components/FieldLibraryModal/standard-field-lists';
import { FormSheet } from '@/shared/ui/FormSheet';
import {
  STANDARD_FIELD_CATEGORIES,
  standardFieldLevel,
  type StandardField,
} from '../../../TaxonomyDetail/components/FieldLibraryModal/standard-fields';

export interface AddStandardFieldsModalProps {
  open: boolean;
  /** Catalog keys already in the library — shown but not selectable. */
  addedKeys: Set<string>;
  /** The org's lists, so the modal can say which ones a selection would create. */
  existingLists: TaxonomyListSummary[];
  isSubmitting: boolean;
  onClose: () => void;
  onAdd: (fields: StandardField[]) => void;
}

/**
 * Pick which standard Fields to add.
 *
 * Mirrors {@link AddStandardListsModal} deliberately — same grouping, same
 * "Select all" checkbox, same unselected-by-default stance. Adding all
 * twenty-four to a curated library shouldn't be what the primary button does
 * by accident.
 *
 * Unlike the Lists picker there's no catalog endpoint: the field catalog ships
 * in the frontend already (it's small, and the builder's picker reads it), so
 * the modal works straight off `STANDARD_FIELD_CATEGORIES`.
 */
export function AddStandardFieldsModal(props: AddStandardFieldsModalProps) {
  const { t } = useTranslation('knowledge');
  const { open, addedKeys, existingLists, isSubmitting, onClose, onAdd } = props;
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const listsToCreate = standardListKeysFor(
    [...selected].filter((key) => !findStandardList(key, existingLists)),
  ).length;

  useEffect(() => {
    if (open) setSelected(new Set());
  }, [open]);

  const available = useMemo(
    () => STANDARD_FIELD_CATEGORIES.flatMap((c) => c.fields).filter((f) => !addedKeys.has(f.key)),
    [addedKeys],
  );
  const allSelected = available.length > 0 && selected.size === available.length;

  const toggle = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const handleAdd = () => {
    if (selected.size === 0) return;
    onAdd(available.filter((f) => selected.has(f.key)));
  };

  return (
    <FormSheet
      className="w-[40rem]"
      open={open}
      onOpenChange={(next) => !next && onClose()}
      title={t('taxonomy.addStandardFieldsModal.addStandardFields')}
    >
      <FormSheet.Body className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
        <p className="text-xs text-muted-foreground">
          {t('taxonomy.addStandardFieldsModal.fieldDefinitionsMonksflowMaintainsEach')}
        </p>

        {available.length > 0 && (
          <label className="flex cursor-pointer items-center gap-2.5 border-b border-border-light pb-3">
            <Checkbox
              checked={allSelected}
              onCheckedChange={() =>
                setSelected(allSelected ? new Set() : new Set(available.map((f) => f.key)))
              }
              aria-label={t('taxonomy.addStandardFieldsModal.selectAllFields')}
            />
            <span className="text-sm font-medium text-foreground">
              {t('taxonomy.addStandardFieldsModal.selectAllCount', { count: available.length })}
            </span>
            {/* The same resolution the add itself performs, run early. Product
                asked for Select all to stay and for its cost to be visible, and
                the cost is the shared lists it seeds — a field whose list the org
                already has adds nothing, which is why the filter matches the
                one in `addStandardFields`. */}
            {listsToCreate > 0 && (
              <span className="ml-auto text-xs text-muted-foreground">
                {t('taxonomy.addStandardFieldsModal.alsoCreatesLists', { count: listsToCreate })}
              </span>
            )}
          </label>
        )}

        {/* No `isSubmitting` spinner over the catalogue: it is an in-memory
            constant, so there is nothing to wait for, and blanking the list
            while the mutation runs hides what the user just chose. The footer's
            own `loading` state already says something is happening. */}
        {STANDARD_FIELD_CATEGORIES.map(({ category, fields }) => (
          <section key={category} className="flex flex-col gap-1.5">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {category}
            </h3>
            <ul className="flex flex-col gap-1">
              {fields.map((field) => {
                const added = addedKeys.has(field.key);
                const level = standardFieldLevel(field.key);
                return (
                  <li key={field.key}>
                    <label
                      className={`flex items-start gap-2.5 rounded-md border border-border-light p-2.5 ${
                        added
                          ? 'opacity-60'
                          : 'cursor-pointer hover:border-primary hover:bg-accent/40'
                      }`}
                    >
                      {added ? (
                        <Check className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                      ) : (
                        <Checkbox
                          checked={selected.has(field.key)}
                          onCheckedChange={() => toggle(field.key)}
                          className="mt-0.5 shrink-0"
                          aria-label={t('taxonomy.addStandardFieldsModal.addField', {
                            name: field.name,
                          })}
                        />
                      )}
                      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-medium text-foreground">{field.name}</span>
                          {level && level !== 'all' && (
                            <Badge
                              variant="ghost"
                              roundness="full"
                              className="border-border-light text-[10px]"
                            >
                              {taxonomyLevelLabel(level)}
                            </Badge>
                          )}
                          {added && (
                            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                              {t('taxonomy.addStandardFieldsModal.alreadyAdded')}
                            </span>
                          )}
                        </span>
                        <span className="truncate text-xs text-muted-foreground">
                          {field.values.length > 0
                            ? t('taxonomy.addStandardFieldsModal.optionCount', {
                                count: field.values.length,
                              })
                            : (field.format ?? field.type.replace('_', ' '))}
                        </span>
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </FormSheet.Body>

      <FormSheet.Footer
        primary={{
          label:
            selected.size === 0
              ? t('taxonomy.addStandardFieldsModal.selectLeastOne')
              : t('taxonomy.addStandardFieldsModal.addFields', { count: selected.size }),
          onClick: handleAdd,
          disabled: selected.size === 0 || isSubmitting,
          loading: isSubmitting,
        }}
        secondary={{ label: t('taxonomy.addStandardFieldsModal.cancel'), onClick: onClose }}
      />
    </FormSheet>
  );
}

AddStandardFieldsModal.displayName = 'AddStandardFieldsModal';
